(() => {
  "use strict";

  if (window.__carbonPriceCarouselsLoaded) return;
  window.__carbonPriceCarouselsLoaded = true;

  const instances = new WeakMap();
  const interactiveSelector = "a, button, input, select, textarea, label, summary, [role='button'], [contenteditable='true']";
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  const directCards = track => Array.from(track.children).filter(child => child instanceof HTMLElement);
  const replaceTokens = (template, values) => Object.entries(values).reduce((text, [token, value]) => text.split(`__${token}__`).join(String(value)), template);
  const cardStart = (track, card) => track.scrollLeft + card.getBoundingClientRect().left - track.getBoundingClientRect().left;
  const scrollCardIntoView = card => card.scrollIntoView({ behavior: reducedMotion.matches ? "auto" : "smooth", block: "nearest", inline: "center" });

  function cardName(card, index, label) {
    const named = card.querySelector("[data-price-card-label], h1, h2, h3, h4, .device-name, .plan-card__provider");
    return named?.textContent?.replace(/\s+/g, " ").trim() || `${label} ${index + 1}`;
  }

  function initSelection(scope, track, kind, cleanups) {
    let cards = directCards(track);
    if (!cards.length) return { refresh: () => {} };

    cards.forEach(card => card.setAttribute("data-price-carousel-card", ""));

    if (kind === "choices") {
      const selectedLabel = scope.dataset.selectedLabel || "Selected";
      const stateFor = card => {
        let state = card.querySelector(":scope > .price-carousel__choice-state");
        if (!state) {
          state = document.createElement("span");
          state.className = "price-carousel__choice-state";
          state.textContent = selectedLabel;
          state.setAttribute("aria-hidden", "true");
          state.hidden = true;
          card.appendChild(state);
        }
        return state;
      };
      const sync = () => {
        cards = directCards(track);
        cards.forEach(card => {
          const checked = Boolean(card.querySelector("input[type='radio']:checked"));
          card.classList.toggle("is-price-selected", checked);
          stateFor(card).hidden = !checked;
        });
      };
      let syncFrame = 0;
      const queueSync = () => {
        if (syncFrame) return;
        syncFrame = requestAnimationFrame(() => {
          syncFrame = 0;
          sync();
        });
      };
      const onTrackChange = event => {
        if (event.target instanceof HTMLInputElement && event.target.type === "radio") queueSync();
      };
      const onRevealInput = event => {
        if (!(event.target instanceof HTMLInputElement) || event.target.type !== "radio") return;
        const card = event.target.closest(".device-card, [data-price-carousel-card]");
        if (card && track.contains(card)) scrollCardIntoView(card);
      };
      track.addEventListener("change", onTrackChange);
      track.addEventListener("focusin", onRevealInput);
      track.addEventListener("invalid", onRevealInput, true);
      cleanups.push(() => track.removeEventListener("change", onTrackChange));
      cleanups.push(() => track.removeEventListener("focusin", onRevealInput));
      cleanups.push(() => track.removeEventListener("invalid", onRevealInput, true));

      const form = scope.closest("form");
      if (form) {
        const onFormChange = () => queueSync();
        const onFormReset = () => queueSync();
        form.addEventListener("change", onFormChange);
        form.addEventListener("reset", onFormReset);
        cleanups.push(() => form.removeEventListener("change", onFormChange));
        cleanups.push(() => form.removeEventListener("reset", onFormReset));
      }
      cleanups.push(() => {
        if (syncFrame) cancelAnimationFrame(syncFrame);
      });
      sync();
      return { refresh: sync };
    }

    const localeSource = scope.matches("[data-price-carousel]") ? scope : document.querySelector("[data-price-carousel]");
    const selectLabel = scope.dataset.selectLabel || localeSource?.dataset.selectLabel || "Select";
    const selectedLabel = scope.dataset.selectedLabel || localeSource?.dataset.selectedLabel || "Selected";
    const label = scope.getAttribute("aria-label") || "Option";
    let selected = cards.find(card => card.classList.contains("is-price-selected")) || null;
    cards.forEach(card => {
      if (card !== selected) card.classList.remove("is-price-selected");
    });

    const boundCards = new WeakSet();
    const boundButtons = new WeakSet();
    let sync = () => {};
    const bindCard = card => {
      let button = card.querySelector(".price-carousel__select");
      if (!button) {
        button = document.createElement("button");
        button.type = "button";
        button.className = "price-carousel__select";
        const host = card.querySelector(":scope > .eero-card__body, :scope > .product-card__body") || card;
        host.appendChild(button);
      }
      if (!boundButtons.has(button)) {
        const onButtonClick = event => {
          event.stopPropagation();
          selected = selected === card ? null : card;
          sync();
        };
        button.addEventListener("click", onButtonClick);
        cleanups.push(() => button.removeEventListener("click", onButtonClick));
        boundButtons.add(button);
      }
      if (!boundCards.has(card)) {
        const onCardClick = event => {
          if (!(event.target instanceof Element) || event.target.closest(interactiveSelector)) return;
          if (selected !== card) {
            selected = card;
            sync();
          }
        };
        card.addEventListener("click", onCardClick);
        cleanups.push(() => card.removeEventListener("click", onCardClick));
        boundCards.add(card);
      }
      return button;
    };

    sync = () => {
      cards = directCards(track);
      if (selected && !cards.includes(selected)) selected = null;
      cards.forEach((card, index) => {
        card.setAttribute("data-price-carousel-card", "");
        const button = bindCard(card);
        const isSelected = card === selected;
        const name = cardName(card, index, label);
        card.classList.toggle("is-price-selected", isSelected);
        const buttonText = isSelected ? selectedLabel : selectLabel;
        if (button.textContent !== buttonText) button.textContent = buttonText;
        button.setAttribute("aria-pressed", String(isSelected));
        button.setAttribute("aria-label", `${isSelected ? selectedLabel : selectLabel}: ${name}`);
      });
    };

    sync();
    return { refresh: sync };
  }

  function initPriceCarousel(scope) {
    if (instances.has(scope)) return;
    const track = scope.querySelector("[data-price-carousel-track]");
    if (!track) return;

    const previous = scope.querySelector("[data-price-carousel-previous]");
    const next = scope.querySelector("[data-price-carousel-next]");
    const position = scope.querySelector("[data-price-carousel-position]");
    const count = scope.querySelector("[data-price-carousel-count]");
    const progress = scope.querySelector("[data-price-carousel-progress]");
    const kind = scope.dataset.priceCarouselKind || "plans";
    const cleanups = [];
    const selection = initSelection(scope, track, kind, cleanups);
    let frame = 0;
    let destroyed = false;
    let suppressClickUntil = 0;
    const drag = { active: false, captured: false, moved: false, pointerId: null, startX: 0, startScroll: 0 };

    const cards = () => directCards(track);
    const setButtonState = (button, disabled) => {
      if (!button) return;
      button.disabled = disabled;
      button.setAttribute("aria-disabled", String(disabled));
    };
    const setPosition = text => {
      if (position && position.textContent !== text) position.textContent = text;
    };

    const refresh = () => {
      frame = 0;
      if (destroyed) return;
      selection.refresh();
      const items = cards();
      const total = items.length;
      const countText = replaceTokens(scope.dataset.countTemplate || "__COUNT__ options", { COUNT: total });
      if (count && count.textContent !== countText) count.textContent = countText;

      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      const hidden = track.clientWidth < 2 || track.getClientRects().length === 0;
      const atStart = hidden || track.scrollLeft <= 2;
      const atEnd = hidden || maxScroll <= 2 || track.scrollLeft >= maxScroll - 2;
      setButtonState(previous, atStart);
      setButtonState(next, atEnd);

      if (!total || hidden) {
        if (progress) progress.style.width = total ? "0%" : "100%";
        return;
      }

      const trackRect = track.getBoundingClientRect();
      const visible = [];
      items.forEach((item, index) => {
        const rect = item.getBoundingClientRect();
        const overlap = Math.max(0, Math.min(rect.right, trackRect.right) - Math.max(rect.left, trackRect.left));
        if (rect.width > 0 && overlap / rect.width >= 0.55) visible.push(index);
      });
      if (!visible.length) {
        const nearest = items.reduce((best, item, index) => Math.abs(cardStart(track, item) - track.scrollLeft) < best.distance ? { index, distance: Math.abs(cardStart(track, item) - track.scrollLeft) } : best, { index: 0, distance: Infinity });
        visible.push(nearest.index);
      }

      const first = visible[0] + 1;
      const last = visible[visible.length - 1] + 1;
      const template = first === last ? scope.dataset.positionSingleTemplate : scope.dataset.positionRangeTemplate;
      setPosition(replaceTokens(template || "Showing __FIRST__–__LAST__ of __TOTAL__", { FIRST: first, LAST: last, TOTAL: total }));
      if (progress) progress.style.width = `${Math.max(0, Math.min(100, last / total * 100))}%`;
    };

    const queueRefresh = () => {
      if (!frame && !destroyed) frame = requestAnimationFrame(refresh);
    };

    const goToIndex = index => {
      const items = cards();
      if (!items.length) return;
      const target = items[Math.max(0, Math.min(items.length - 1, index))];
      track.scrollTo({ left: cardStart(track, target), behavior: reducedMotion.matches ? "auto" : "smooth" });
    };

    const nearestIndex = () => {
      const items = cards();
      return items.reduce((best, item, index) => {
        const distance = Math.abs(cardStart(track, item) - track.scrollLeft);
        return distance < best.distance ? { index, distance } : best;
      }, { index: 0, distance: Infinity }).index;
    };

    const move = direction => goToIndex(nearestIndex() + direction);
    const onPrevious = () => move(-1);
    const onNext = () => move(1);
    const onKeyDown = event => {
      if (event.target !== track) return;
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        move(-1);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        move(1);
      } else if (event.key === "Home") {
        event.preventDefault();
        goToIndex(0);
      } else if (event.key === "End") {
        event.preventDefault();
        goToIndex(cards().length - 1);
      }
    };
    const onPointerDown = event => {
      if (event.pointerType !== "mouse" || event.button !== 0 || track.scrollWidth <= track.clientWidth + 2) return;
      drag.active = true;
      drag.moved = false;
      drag.captured = false;
      drag.pointerId = event.pointerId;
      drag.startX = event.clientX;
      drag.startScroll = track.scrollLeft;
    };
    const onPointerMove = event => {
      if (!drag.active || event.pointerId !== drag.pointerId) return;
      const delta = event.clientX - drag.startX;
      if (!drag.moved && Math.abs(delta) < 6) return;
      drag.moved = true;
      if (!drag.captured) {
        try {
          track.setPointerCapture(event.pointerId);
          drag.captured = true;
        } catch (_error) {
          drag.captured = false;
        }
      }
      track.classList.add("is-pointer-dragging");
      event.preventDefault();
      track.scrollLeft = drag.startScroll - delta;
    };
    const finishPointer = (event, cancelled = false) => {
      if (!drag.active || event.pointerId !== drag.pointerId) return;
      const moved = drag.moved;
      const pointerId = drag.pointerId;
      drag.active = false;
      drag.moved = false;
      drag.captured = false;
      drag.pointerId = null;
      track.classList.remove("is-pointer-dragging");
      if (track.hasPointerCapture(pointerId)) track.releasePointerCapture(pointerId);
      if (moved && !cancelled) {
        suppressClickUntil = Date.now() + 450;
        requestAnimationFrame(() => goToIndex(nearestIndex()));
      }
    };
    const onPointerUp = event => finishPointer(event);
    const onPointerCancel = event => finishPointer(event, true);
    const onDragStart = event => {
      if (drag.active) event.preventDefault();
    };
    const onLostPointerCapture = event => {
      if (drag.active && event.pointerId === drag.pointerId) finishPointer(event, true);
    };
    const onClickCapture = event => {
      if (Date.now() < suppressClickUntil) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };

    previous?.addEventListener("click", onPrevious);
    next?.addEventListener("click", onNext);
    track.addEventListener("keydown", onKeyDown);
    track.addEventListener("scroll", queueRefresh, { passive: true });
    track.addEventListener("pointerdown", onPointerDown);
    track.addEventListener("pointermove", onPointerMove);
    track.addEventListener("pointerup", onPointerUp);
    track.addEventListener("pointercancel", onPointerCancel);
    track.addEventListener("dragstart", onDragStart);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerCancel);
    track.addEventListener("lostpointercapture", onLostPointerCapture);
    track.addEventListener("click", onClickCapture, true);
    cleanups.push(() => previous?.removeEventListener("click", onPrevious));
    cleanups.push(() => next?.removeEventListener("click", onNext));
    cleanups.push(() => track.removeEventListener("keydown", onKeyDown));
    cleanups.push(() => track.removeEventListener("scroll", queueRefresh));
    cleanups.push(() => track.removeEventListener("pointerdown", onPointerDown));
    cleanups.push(() => track.removeEventListener("pointermove", onPointerMove));
    cleanups.push(() => track.removeEventListener("pointerup", onPointerUp));
    cleanups.push(() => track.removeEventListener("pointercancel", onPointerCancel));
    cleanups.push(() => track.removeEventListener("dragstart", onDragStart));
    cleanups.push(() => window.removeEventListener("pointerup", onPointerUp));
    cleanups.push(() => window.removeEventListener("pointercancel", onPointerCancel));
    cleanups.push(() => track.removeEventListener("lostpointercapture", onLostPointerCapture));
    cleanups.push(() => track.removeEventListener("click", onClickCapture, true));

    if ("ResizeObserver" in window) {
      const resizeObserver = new ResizeObserver(queueRefresh);
      resizeObserver.observe(track);
      cleanups.push(() => resizeObserver.disconnect());
    } else {
      window.addEventListener("resize", queueRefresh, { passive: true });
      cleanups.push(() => window.removeEventListener("resize", queueRefresh));
    }

    const destroy = () => {
      if (destroyed) return;
      destroyed = true;
      if (frame) cancelAnimationFrame(frame);
      track.classList.remove("is-pointer-dragging");
      cleanups.splice(0).forEach(cleanup => cleanup());
      instances.delete(scope);
    };
    instances.set(scope, { destroy });
    queueRefresh();
  }

  function initHomeSelection(scope) {
    if (instances.has(scope)) return;
    const track = scope.querySelector("[data-carousel-track]");
    if (!track) return;
    const cleanups = [];
    const selection = initSelection(scope, track, "plans", cleanups);
    const destroy = () => {
      cleanups.splice(0).forEach(cleanup => cleanup());
      instances.delete(scope);
    };
    instances.set(scope, { destroy, refresh: selection.refresh });
  }

  function scopesWithin(root, selector) {
    if (!(root instanceof Element) && root !== document) return [];
    const found = [];
    if (root instanceof Element && root.matches(selector)) found.push(root);
    root.querySelectorAll(selector).forEach(node => found.push(node));
    return found;
  }

  function initWithin(root) {
    scopesWithin(root, "[data-price-carousel]").forEach(initPriceCarousel);
    scopesWithin(root, "[data-plan-carousel]").forEach(initHomeSelection);
  }

  function destroyWithin(root) {
    scopesWithin(root, "[data-price-carousel], [data-plan-carousel]").forEach(scope => instances.get(scope)?.destroy());
  }

  function boot() {
    initWithin(document);
    document.addEventListener("shopify:section:load", event => initWithin(event.target));
    document.addEventListener("shopify:section:unload", event => destroyWithin(event.target));
    const observer = new MutationObserver(records => {
      const removed = [];
      const added = [];
      records.forEach(record => {
        record.removedNodes.forEach(node => {
          if (node instanceof Element) removed.push(node);
        });
        record.addedNodes.forEach(node => {
          if (node instanceof Element) added.push(node);
        });
      });
      removed.forEach(destroyWithin);
      added.forEach(initWithin);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot, { once: true });
  else boot();
})();
